import { useEffect } from "react";

interface SEOOptions {
  title: string;
  description: string;
  path: string;
  image?: string;
}

const BASE_URL = "https://apexride.racing";

function setMeta(selector: string, attr: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    const [type, name] = selector.replace(/[\[\]"']/g, "").split("=");
    if (type === "meta name" || selector.includes("name=")) {
      el.setAttribute("name", name);
    } else {
      el.setAttribute("property", name);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Per-route SEO: sets document title, meta description, canonical and og/twitter tags.
 * Keep title under 60 chars and description between 50-160 chars.
 */
export function useSEO({ title, description, path, image }: SEOOptions) {
  useEffect(() => {
    const url = `${BASE_URL}${path}`;
    document.title = title;

    const setByName = (name: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setByProp = (prop: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setByName("description", description);
    setByName("twitter:title", title);
    setByName("twitter:description", description);
    setByName("twitter:url", url);
    setByProp("og:title", title);
    setByProp("og:description", description);
    setByProp("og:url", url);
    if (image) {
      setByProp("og:image", image);
      setByName("twitter:image", image);
    }
    setLink("canonical", url);
  }, [title, description, path, image]);
}
